/**
 * What a file cell holds.
 *
 * Pure, and shared by the cell and the editor. The two draw the same files at
 * different sizes and would otherwise each decide for themselves what counts as
 * a file and what one is called.
 */
import type { UserFileValue } from '../files/requests'

/**
 * The files in a value, keeping only the ones the backend can be told about.
 *
 * A file is named by an opaque string it hands out, so an entry without one is
 * an entry nothing can be written from. The same rule reads a stored value and
 * a draft, because they are the same shape.
 */
export function normaliseFiles(value: unknown): UserFileValue[] {
  if (!Array.isArray(value)) return []
  return value.filter(
    (file): file is UserFileValue =>
      typeof (file as UserFileValue | null)?.name === 'string',
  )
}

/** The files a draft holds, or none when it says nothing readable. */
export function parseFiles(draft: string): UserFileValue[] {
  if (draft.trim() === '') return []
  try {
    return normaliseFiles(JSON.parse(draft))
  } catch {
    return []
  }
}

/** What a file is called on screen. */
export function fileLabel(file: UserFileValue): string {
  const shown = file.visible_name ?? file.original_name ?? ''
  return shown === '' ? file.name : shown
}

/**
 * The picture to draw a file as, or nothing when there is none to draw.
 *
 * Only ever a thumbnail, never the file itself. The backend makes three -- 21
 * pixels tall, 48 square, and 300x160 for the top of a card -- and falling back
 * to the original would put a five-megabyte photograph in a twenty-two pixel
 * box, once per row on screen. A file with no thumbnail is drawn as its name
 * instead.
 *
 * `is_image` is not consulted, although it is right there on the value. The
 * serializer answers `thumbnails` with nothing at all unless the file is an
 * image, so the two can never disagree -- and the question being asked is not
 * whether this is a picture but whether there is one to draw.
 */
export function fileThumbnail(
  file: UserFileValue,
  size: 'tiny' | 'small' | 'card_cover',
): string | null {
  return file.thumbnails?.[size]?.url ?? null
}
